import { Link } from 'react-router';
import { useState } from 'react';
import type { Child, Compliance, StateRequirement } from '../lib/types';
import { RequiredSubjects } from './RequiredSubjects';

/**
 * Homeschool state requirements (#599, phase 1 of #453): once the family turns on state compliance (#954) and picks its
 * state and legal option, each child's State compliance section shows days and hours against it, and Home flags a
 * child who's behind.
 */

const fmtDate = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const num = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });
const hoursLabel = (h: number) => (h === 4.5 ? '4½' : num(h));

/** In words: "180 days, at least 4½ hours each", "180 days or 900 hours", "1,000 hours (600 in core subjects)". */
export function ruleText(r: StateRequirement): string {
  const days = r.days ? `${num(r.days)} days` : '';
  const per = r.hoursPerDay ? (r.perDay === 'average' ? `averaging ${hoursLabel(r.hoursPerDay)} hours a day` : `at least ${hoursLabel(r.hoursPerDay)} hours each`) : '';
  const hours = r.hours ? `${num(r.hours)} hours${r.coreHours ? ` (${num(r.coreHours)} in core subjects)` : ''}` : '';
  if (days && hours) return `${days} ${r.either ? 'or' : 'and'} ${hours}`;
  if (days) return per ? `${days}, ${per}` : days;
  if (hours) return hours;
  return 'No day or hour count';
}

/** Picking the state and how the family homeschools (#599): in the opt-in sheet and on Where you file (#954). */
export function StateChoice({ requirements, selectedId, onPick }: { requirements: StateRequirement[]; selectedId: string; onPick: (id: string) => void }) {
  const [state, setState] = useState(requirements.find((r) => r.id === selectedId)?.state ?? '');
  const states = [...new Map(requirements.map((r) => [r.state, r.stateName])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const options = requirements.filter((r) => r.state === state);
  const selected = requirements.find((r) => r.id === selectedId);
  return (
    <div className="stack">
      <label>
        Your state
        <select
          value={state}
          onChange={(e) => {
            setState(e.target.value);
            const only = requirements.filter((r) => r.state === e.target.value);
            if (only.length === 1) onPick(only[0]!.id);
          }}
        >
          <option value="">Pick your state</option>
          {states.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      </label>
      {state && (
        <div className="stack state-options" role="radiogroup" aria-label="How you homeschool">
          <span className="field-label">How you homeschool</span>
          {options.map((r) => (
            <button key={r.id} type="button" role="radio" aria-checked={r.id === selectedId} className={`state-option ${r.id === selectedId ? 'on' : ''}`} onClick={() => onPick(r.id)}>
              <strong>{r.option}</strong>
              <span className="muted small">{ruleText(r)}</span>
            </button>
          ))}
        </div>
      )}
      {selected && selected.state === state && (
        <p className="muted small">
          {selected.notes}{' '}
          {selected.sourceUrl && (
            <a href={selected.sourceUrl} target="_blank" rel="noreferrer">
              Source
            </a>
          )}
          {selected.reviewedAt && <> · Last reviewed {fmtDate(selected.reviewedAt)}</>} · Not legal advice: check with your state or your
          accountability association.
        </p>
      )}
    </div>
  );
}

/** Where the state sets no number, the family's umbrella school may (#603): days and/or hours to track. */
export function CustomTarget({ days, hours, onSave }: { days: number; hours: number; onSave: (days: number, hours: number) => void }) {
  const [d, setD] = useState(days ? String(days) : '');
  const [h, setH] = useState(hours ? String(hours) : '');
  const dirty = (Number(d) || 0) !== days || (Number(h) || 0) !== hours;
  return (
    <div className="stack">
      <span className="muted small">Your state sets no day or hour count for this option. If your umbrella school sets one, enter it to track it.</span>
      <div className="field-row">
        <label>
          Days a year
          <input type="number" min={0} max={366} value={d} onChange={(e) => setD(e.target.value)} placeholder="e.g. 180" />
        </label>
        <label>
          Hours a year
          <input type="number" min={0} max={3000} value={h} onChange={(e) => setH(e.target.value)} placeholder="optional" />
        </label>
      </div>
      {dirty && (
        <button className="btn small" onClick={() => onSave(Math.round(Number(d) || 0), Math.round(Number(h) || 0))}>
          Save target
        </button>
      )}
    </div>
  );
}

const STATUS: Record<string, { label: string; tone: string }> = {
  met: { label: 'Requirement met', tone: 'good' },
  'on-track': { label: 'On track', tone: 'good' },
  behind: { label: 'Behind', tone: 'warn' },
  'not-started': { label: 'School year not started', tone: '' },
  'no-target': { label: 'No day or hour count', tone: '' },
};

function Bar({ label, value, target, expected, unit }: { label: string; value: number; target: number; expected: number | null; unit: string }) {
  const pct = (n: number) => `${Math.min(100, Math.max(0, (n / target) * 100))}%`;
  return (
    <div className="compliance-bar">
      <div className="row-between">
        <strong>{label}</strong>
        <span>
          <strong>{num(value)}</strong> of {num(target)} {unit}
        </span>
      </div>
      <div className="compliance-track" role="img" aria-label={`${num(value)} of ${num(target)} ${unit}${expected !== null ? `; ${num(expected)} expected by today` : ''}`}>
        <div className="compliance-fill" style={{ width: pct(value) }} />
        {expected !== null && expected < target && <div className="compliance-mark" style={{ left: pct(expected) }} />}
      </div>
    </div>
  );
}

/** State compliance → Progress (#954): days and hours against the state's requirement, with the pace mark and a nudge when behind. */
export function ComplianceCard({ child }: { child: Child }) {
  const c = child.compliance as Compliance | null | undefined;
  if (!c) return null;
  const r = c.requirement;
  const title = `${r.stateName} · ${r.option}`;
  if (c.needsSchoolYear) {
    return (
      <section className="card stack compliance">
        <h2>{title}</h2>
        <p className="muted">
          Set your school year’s first and last day in <Link to="/settings">Settings → School Settings</Link> to see {child.name}’s progress toward{' '}
          {ruleText(r).toLowerCase()}.
        </p>
      </section>
    );
  }
  const st = STATUS[c.status] ?? STATUS['on-track']!;
  const minPerDay = r.hoursPerDay && r.perDay === 'minimum';
  return (
    <section className="card stack compliance">
      <div className="row-between wrap">
        <h2>{title}</h2>
        <span className={`tag ${st.tone}`}>{c.status === 'behind' ? `Behind by ${c.behindBy} day${c.behindBy === 1 ? '' : 's'}` : st.label}</span>
      </div>
      <p className="muted small">
        {ruleText(r)} this school year ({fmtDate(c.schoolYear.start)} – {fmtDate(c.schoolYear.end)}). Counted from the hours you record on the Attendance tab.
      </p>
      {r.days && <Bar label="Days" value={c.days} target={r.days} expected={c.expectedDays} unit="days" />}
      {c.hoursTarget && <Bar label="Hours" value={c.hours} target={c.hoursTarget} expected={c.expectedHours} unit="hours" />}
      {!!c.coreTarget && (
        <>
          <Bar label="Core subjects" value={c.coreHours ?? 0} target={c.coreTarget} expected={null} unit="hours" />
          <p className="muted small">
            Hours split into {child.name}’s core subjects ({(child.coreSubjects ?? []).join(', ') || 'none marked yet'}) on the Attendance tab. Set which count as core in
            Profile → Subjects.
          </p>
        </>
      )}
      {c.status === 'no-target' && (
        <p>
          <strong>{num(c.days)}</strong> days and <strong>{num(c.hours)}</strong> hours recorded so far.
        </p>
      )}
      {minPerDay && c.shortDays > 0 && (
        <p className="muted small">
          {c.shortDays} shorter day{c.shortDays === 1 ? '' : 's'} (under {hoursLabel(r.hoursPerDay!)} hours) {c.shortDays === 1 ? 'doesn’t' : 'don’t'} count toward the{' '}
          {num(r.days ?? 0)}.
        </p>
      )}
      {(c.expectedDays !== null || c.expectedHours !== null) && c.status !== 'met' && <p className="muted small">The yellow mark is where {child.name} should be today to finish on time.</p>}
      {c.status === 'behind' && (
        <p className="compliance-warn">
          {child.name} is {c.behindBy} school day{c.behindBy === 1 ? '' : 's'} behind, with {c.schoolDaysLeft} school days left this year.
          {r.either && ' Meeting either the days or the hours is enough.'}
        </p>
      )}
      <RequiredSubjects child={child} />
      <p className="muted small">
        {c.estimateOnly > 0 && (
          <>
            {c.estimateOnly} day{c.estimateOnly === 1 ? ' has' : 's have'} only the app’s estimate: record {c.estimateOnly === 1 ? 'its' : 'their'} hours so{' '}
            {c.estimateOnly === 1 ? 'it counts' : 'they count'}.{' '}
          </>
        )}
        Not legal advice; check your state’s rules.{' '}
        {r.sourceUrl && (
          <a href={r.sourceUrl} target="_blank" rel="noreferrer">
            Source
          </a>
        )}
      </p>
    </section>
  );
}

/** Home: a small note on a child's card, only while they're behind. */
export function ComplianceNote({ child }: { child: Child }) {
  const c = child.compliance as Compliance | null | undefined;
  if (!c || c.needsSchoolYear || c.status !== 'behind') return null;
  const r = c.requirement;
  return (
    <p className="compliance-warn small">
      {c.behindBy} day{c.behindBy === 1 ? '' : 's'} behind {r.stateName}’s {r.days ? `${num(r.days)} days` : `${num(c.hoursTarget ?? 0)} hours`}.{' '}
      <Link to={`/kids/${child.id}/compliance`}>See state compliance</Link>
    </p>
  );
}
